/**
 * @lodex-pro/web-portal/nav/DisposalShell — minimal shell for the disposal subtree.
 *
 * Phase 16 / DEC-159 — the operator PortalShell (nav/PortalShell.tsx) uses
 * `usePortalMe()` which hits `/v1/operator-portal/me` — that endpoint 401s /
 * 404s for a disposal-pool user because they aren't scoped to an operator
 * tenant. Rather than teach PortalShell about two tenant types (it's already
 * dense), the disposal subtree gets its own minimal shell for now.
 *
 * Purpose: a top bar with the `lodeX Pro Disposal Portal` brand + a sign-out
 * button, wrapping `<Outlet />` for child routes. NO sidebar (there's only one
 * route until 16.4+ modules land). NO tenant-name + tier badge (no /me
 * endpoint to source them from yet).
 *
 * When the real disposal modules ship (Phase 16.4+), this shell may be
 * upgraded to fetch a `/v1/disposal-portal/me`-equivalent and render a
 * sidebar. Until then, this is intentionally lightweight — every design
 * decision here is "just enough to prove sign-in works end-to-end".
 *
 * Spec: DEC-159; DEC-158 (disposal activation lifecycle); DEC-005 (10 modules).
 */

import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';

export function DisposalShell(): React.JSX.Element {
  const navigate = useNavigate();

  const handleSignOut = (): void => {
    void (async (): Promise<void> => {
      try {
        await signOut({ global: false });
      } finally {
        void navigate('/portal/disposal/sign-in', { replace: true });
      }
    })();
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        background: 'var(--color-bg-primary)',
        color: 'var(--color-text-primary)',
      }}
    >
      <header
        aria-label="Disposal portal header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 24px',
          borderBottom: '1px solid var(--color-border-primary)',
          background: 'var(--color-bg-rail)',
        }}
      >
        <div style={{ fontWeight: 700, fontSize: '15px' }}>lodeX Pro Disposal Portal</div>
        <button
          type="button"
          onClick={handleSignOut}
          aria-label="Sign out"
          style={{
            padding: '6px 14px',
            minHeight: '32px',
            cursor: 'pointer',
            background: 'var(--color-bg-tertiary)',
            color: 'var(--color-text-primary)',
            border: '1px solid var(--color-border-secondary)',
            borderRadius: 'var(--radius-pill)',
            fontSize: '13px',
          }}
        >
          Sign out
        </button>
      </header>
      <div style={{ flex: 1 }}>
        <Outlet />
      </div>
    </div>
  );
}
